import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { generateMCQTest } from '../services/careerService';
import { db } from '../firebase/firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { customToast } from '../utils/toastUtils';
import './AIImprovementPage.css';
import './MCQTest.css';

const MCQTest = () => {
  const { userData } = useUser();
  const navigate = useNavigate();

  const STORAGE_KEY = 'ai_mcq_test_state';

  // Refs for scroll navigation
  const inputSectionRef = useRef(null);
  const testQuestionsRef = useRef(null);
  const testResultsRef = useRef(null);
  
  // Form state — restored from sessionStorage
  const [jobTitle, setJobTitle] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.jobTitle || ''; } catch { return ''; }
  });
  const [jobDomains, setJobDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [skills, setSkills] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.skills || ''; } catch { return ''; }
  });
  const [difficultyLevel, setDifficultyLevel] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.difficultyLevel || ''; } catch { return ''; }
  });

  // Test state — restored from sessionStorage
  const [loading, setLoading] = useState(false);
  const [testData, setTestData] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.testData || null; } catch { return null; }
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.currentQuestionIndex || 0; } catch { return 0; }
  });
  const [userAnswers, setUserAnswers] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.userAnswers || {}; } catch { return {}; }
  });
  const [showResults, setShowResults] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.showResults || false; } catch { return false; }
  });
  const [results, setResults] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.results || null; } catch { return null; }
  });

  // Persist to sessionStorage on every relevant state change
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      jobTitle, skills, difficultyLevel,
      testData, currentQuestionIndex, userAnswers, showResults, results
    }));
  }, [jobTitle, skills, difficultyLevel, testData, currentQuestionIndex, userAnswers, showResults, results]);

  // Fetch job domains on component mount
  useEffect(() => {
    const fetchJobDomains = async () => {
      try {
        setLoadingDomains(true);
        
        // Fetch directly from Firestore - no compound query to avoid index requirement
        const jobDomainsRef = collection(db, 'jobDomains');
        const querySnapshot = await getDocs(jobDomainsRef);
        
        const domains = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Only include active domains
          if (data.isActive) {
            domains.push({
              id: doc.id,
              ...data
            });
          }
        });
        
        // Sort by order field in JavaScript (no index needed)
        domains.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setJobDomains(domains);
        
      } catch (error) {
        console.error('Error fetching job domains:', error);
        customToast.error('Failed to load job domains');
      } finally {
        setLoadingDomains(false);
      }
    };

    fetchJobDomains();
  }, []);

  const handleGenerateTest = async () => {
    // Validate required fields
    if (!jobTitle || !difficultyLevel) {
      customToast.error('Job title and difficulty level are required');
      return;
    }

    if (!userData?.uid) {
      customToast.error('Please login to use this feature');
      return;
    }

    setLoading(true);
    
    try {
      const response = await generateMCQTest({
        userId: userData.uid,
        jobTitle,
        skills,
        difficultyLevel
      });

      if (response.success) {
        setTestData(response.data);
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setShowResults(false);
        setResults(null);
        customToast.success('Test generated successfully! Good luck!');
        
        // Scroll to test questions section
        setTimeout(() => {
          testQuestionsRef.current?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }, 500);
      } else {
        customToast.error(response.error || 'Failed to generate test');
      }
    } catch (error) {
      console.error('❌ MCQ test error:', error);
      customToast.error(error.message || 'Failed to generate test');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < testData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitTest = () => {
    // Check if all questions are answered
    const answeredCount = Object.keys(userAnswers).length;
    if (answeredCount < testData.questions.length) {
      customToast.warning(`Please answer all questions. ${answeredCount}/${testData.questions.length} answered.`);
      return;
    }

    // Calculate results
    calculateResults();
  };

  const calculateResults = () => {
    const questions = testData.questions;
    let correctCount = 0;
    const skillCategoryScores = {};
    const detailedAnswers = [];

    questions.forEach(question => {
      const userAnswer = userAnswers[question.id];
      const isCorrect = userAnswer === question.answer;
      
      if (isCorrect) {
        correctCount++;
      }

      // Track skill category performance
      const category = question.skillCategory;
      if (!skillCategoryScores[category]) {
        skillCategoryScores[category] = { correct: 0, total: 0 };
      }
      skillCategoryScores[category].total++;
      if (isCorrect) {
        skillCategoryScores[category].correct++;
      }

      detailedAnswers.push({
        questionId: question.id,
        question: question.question,
        userAnswer,
        correctAnswer: question.answer,
        isCorrect,
        explanation: question.explanation,
        skillCategory: category
      });
    });

    const score = Math.round((correctCount / questions.length) * 100);
    const passed = score >= (testData.testMetadata?.passingScore || 60);

    // Identify strengths and weaknesses
    const strengths = [];
    const weaknesses = [];

    Object.entries(skillCategoryScores).forEach(([category, scores]) => {
      const percentage = (scores.correct / scores.total) * 100;
      const categoryResult = {
        category,
        correct: scores.correct,
        total: scores.total,
        percentage: Math.round(percentage)
      };

      if (percentage >= 75) {
        strengths.push(categoryResult);
      } else if (percentage < 50) {
        weaknesses.push(categoryResult);
      }
    });

    setResults({
      score,
      correctCount,
      totalQuestions: questions.length,
      passed,
      strengths,
      weaknesses,
      detailedAnswers,
      skillCategoryScores
    });

    setShowResults(true);

    // Scroll to results
    setTimeout(() => {
      testResultsRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 300);
  };

  const currentQuestion = testData?.questions?.[currentQuestionIndex];
  const progress = testData ? ((currentQuestionIndex + 1) / testData.questions.length) * 100 : 0;

  return (
    <div className="tool-page">
      {/* Hero Section */}
      <div className="mcq-test-hero">
        <div className="mcq-test-hero-content">
          <div className="hero-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Take MCQ Test</span>
          </div>
          <h1 className="mcq-test-title">
            Evaluate Your Real-World Knowledge and Career Readiness
          </h1>
          <p className="mcq-test-tagline">
            AI-generated, role-specific assessments tailored to your skills and experience level
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="mcq-test-content">
        {/* Description Card */}
        <div className="mcq-test-description-card">
          <div className="description-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="url(#descGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <defs>
                <linearGradient id="descGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8b5cf6"/>
                  <stop offset="100%" stopColor="#7c3aed"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <p className="mcq-test-description">
            The MCQ Test feature helps you measure your understanding of your job role through intelligent assessments 
            based on your job title, skills, and difficulty level. The test evaluates your strengths and identifies knowledge 
            gaps, giving you a clear picture of your readiness for interviews and career progression. Based on your 
            performance, you receive insights that help you improve your expertise and prepare more effectively for real-world 
            job opportunities.
          </p>
        </div>

        {/* Input form and info sections are ALWAYS visible */}
        <>
            {/* Input Form Section */}
            <div className="mcq-test-form-section" ref={inputSectionRef}>
              <h2 className="section-title">Start Your Assessment</h2>
              <div className="mcq-test-form">
                <div className="form-group">
                  <label className="form-label">
                    Job Domain <span className="required">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    disabled={loadingDomains}
                  >
                    <option value="">
                      {loadingDomains ? 'Loading job domains...' : 'Select a job domain'}
                    </option>
                    
                    {/* Group domains by category */}
                    {['Software Engineering', 'Engineering', 'Industry Sectors'].map(category => {
                      const categoryDomains = jobDomains.filter(d => d.category === category);
                      if (categoryDomains.length === 0) return null;
                      
                      return (
                        <optgroup key={category} label={category}>
                          {categoryDomains.map((domain) => (
                            <option key={domain.id} value={domain.name}>
                              {domain.name}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                  <span className="form-hint">
                    {jobDomains.length > 0 ? `${jobDomains.length} domains available` : 'Loading domains...'}
                  </span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Skills You Know <span className="optional-label">(Optional)</span>
                  </label>
                  <textarea
                    className="form-textarea"
                    placeholder="e.g., JavaScript, Python, React, Data Analysis..."
                    rows="3"
                    value={skills}
                    onChange={(e) => setSkills(e.target.value)}
                  />
                  <span className="form-hint">Add skills to get more focused questions</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Difficulty Level <span className="required">*</span>
                  </label>
                  <select 
                    className="form-select"
                    value={difficultyLevel}
                    onChange={(e) => setDifficultyLevel(e.target.value)}
                  >
                    <option value="">Select difficulty level</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>

                <button 
                  className="mcq-test-cta-button"
                  onClick={handleGenerateTest}
                  disabled={!jobTitle || !difficultyLevel || loading || loadingDomains}
                >
                  {loading ? (
                    <>
                      <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                        <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                      </svg>
                      Generating Test...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Take Assessment
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* How It Works & What You Get - hidden when test/results are active */}
            {!testData && !showResults && (
            <>
            <div className="how-it-works-section">
              <h2 className="section-title">How It Works</h2>
              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <h3>Select Job Domain</h3>
                  <p>Choose from available job domains and sectors</p>
                </div>

                <div className="step-card">
                  <div className="step-number">2</div>
                  <h3>Add Skills You Know</h3>
                  <p>Optional: List your skills for focused questions</p>
                </div>

                <div className="step-card">
                  <div className="step-number">3</div>
                  <h3>Choose Difficulty Level</h3>
                  <p>Select Beginner, Intermediate, or Advanced</p>
                </div>

                <div className="step-card">
                  <div className="step-number">4</div>
                  <h3>Click Take Assessment</h3>
                  <p>AI generates 15 role-specific questions</p>
                </div>

                <div className="step-card">
                  <div className="step-number">5</div>
                  <h3>Take the MCQ Test</h3>
                  <p>Answer all 15 multiple-choice questions</p>
                </div>

                <div className="step-card">
                  <div className="step-number">6</div>
                  <h3>Get Performance Analysis</h3>
                  <p>Receive instant results and recommendations</p>
                </div>
              </div>
            </div>

            {/* What You Get Section */}
            <div className="what-you-get-section">
              <h2 className="section-title">What You Get</h2>
              <div className="benefits-grid">
                <div className="benefit-card">
                  <div className="benefit-icon purple">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Role-Specific Questions</h3>
                  <p>Tailored to your job level and expertise.</p>
                </div>

                <div className="benefit-card">
                  <div className="benefit-icon blue">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Instant Score Report</h3>
                  <p>Clear breakdown of your performance.</p>
                </div>

                <div className="benefit-card">
                  <div className="benefit-icon green">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Skill Strength Analysis</h3>
                  <p>See where you perform strongly.</p>
                </div>

                <div className="benefit-card">
                  <div className="benefit-icon orange">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Weak Area Identification</h3>
                  <p>Pinpoint improvement areas.</p>
                </div>

                <div className="benefit-card">
                  <div className="benefit-icon teal">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Learning Recommendations</h3>
                  <p>Get directed to relevant AI Career Toolkit suggestions.</p>
                </div>
              </div>
            </div>
            </>
            )}
          </>

        {/* Test Questions Section */}
        {testData && !showResults && (
          <div id="mcq-test-questions" ref={testQuestionsRef} className="test-questions-section">
            <div className="test-header">
              <h2 className="section-title">Assessment in Progress</h2>
              <div className="test-meta">
                <span className="test-difficulty">{testData.testMetadata?.difficultyLevel || difficultyLevel}</span>
                <span className="test-time">⏱️ {testData.testMetadata?.estimatedTime || '15-20 mins'}</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="test-progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <div className="test-progress-text">
              Question {currentQuestionIndex + 1} of {testData.questions.length}
            </div>

            {/* Current Question */}
            {currentQuestion && (
              <div className="question-card">
                <div className="question-header">
                  <span className="question-number">Question {currentQuestion.id}</span>
                  <span className="question-category">{currentQuestion.skillCategory}</span>
                </div>
                
                <h3 className="question-text">{currentQuestion.question}</h3>

                <div className="options-container">
                  {Object.entries(currentQuestion.options).map(([key, value]) => (
                    <div
                      key={key}
                      className={`option-card ${userAnswers[currentQuestion.id] === key ? 'selected' : ''}`}
                      onClick={() => handleAnswerSelect(currentQuestion.id, key)}
                    >
                      <div className="option-letter">{key}</div>
                      <div className="option-text">{value}</div>
                    </div>
                  ))}
                </div>

                <div className="question-navigation">
                  <button
                    className="nav-button prev"
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestionIndex === 0}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Previous
                  </button>

                  {currentQuestionIndex < testData.questions.length - 1 ? (
                    <button
                      className="nav-button next"
                      onClick={handleNextQuestion}
                      disabled={!userAnswers[currentQuestion.id]}
                    >
                      Next
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  ) : (
                    <button
                      className="nav-button submit"
                      onClick={handleSubmitTest}
                    >
                      Submit Test
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>

                {/* Answer Summary */}
                <div className="answer-summary">
                  <p>Answered: {Object.keys(userAnswers).length} / {testData.questions.length}</p>
                  <div className="answer-dots">
                    {testData.questions.map((q, idx) => (
                      <span
                        key={q.id}
                        className={`answer-dot ${userAnswers[q.id] ? 'answered' : ''} ${idx === currentQuestionIndex ? 'current' : ''}`}
                        onClick={() => setCurrentQuestionIndex(idx)}
                        title={`Question ${q.id}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results Section */}
        {showResults && results && (
          <div id="mcq-test-results" ref={testResultsRef} className="results-container">
            <div className="results-header">
              <h2 className="results-title">Your Assessment Results</h2>
            </div>

            {/* Score Card */}
            <div className="score-card">
              <div className="score-circle">
                <svg width="200" height="200" viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="90" fill="none" stroke="#e2e8f0" strokeWidth="12"/>
                  <circle 
                    cx="100" 
                    cy="100" 
                    r="90" 
                    fill="none" 
                    stroke={results.passed ? '#10b981' : '#f59e0b'}
                    strokeWidth="12"
                    strokeDasharray={`${(results.score / 100) * 565.48} 565.48`}
                    strokeLinecap="round"
                    transform="rotate(-90 100 100)"
                  />
                </svg>
                <div className="score-text">
                  <div className="score-value">{results.score}%</div>
                  <div className="score-label">{results.passed ? 'PASSED' : 'NEEDS IMPROVEMENT'}</div>
                </div>
              </div>

              <div className="score-details">
                <div className="score-stat">
                  <span className="stat-label">Correct Answers</span>
                  <span className="stat-value">{results.correctCount} / {results.totalQuestions}</span>
                </div>
                <div className="score-stat">
                  <span className="stat-label">Passing Score</span>
                  <span className="stat-value">{testData.testMetadata?.passingScore || 60}%</span>
                </div>
                <div className="score-stat">
                  <span className="stat-label">Your Performance</span>
                  <span className="stat-value">{results.passed ? '✓ Excellent' : '⚠ Need Practice'}</span>
                </div>
              </div>
            </div>

            {/* Skill Analysis */}
            <div className="results-section">
              <h3 className="section-subtitle">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" stroke="url(#analysisGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="analysisGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#8b5cf6"/>
                      <stop offset="100%" stopColor="#7c3aed"/>
                    </linearGradient>
                  </defs>
                </svg>
                Performance by Skill Category
              </h3>

              <div className="skill-categories">
                {Object.entries(results.skillCategoryScores).map(([category, scores]) => {
                  const percentage = (scores.correct / scores.total) * 100;
                  let categoryClass = 'average';
                  if (percentage >= 75) categoryClass = 'strong';
                  else if (percentage < 50) categoryClass = 'weak';

                  return (
                    <div key={category} className={`category-bar ${categoryClass}`}>
                      <div className="category-info">
                        <span className="category-name">{category}</span>
                        <span className="category-score">{scores.correct}/{scores.total} ({Math.round(percentage)}%)</span>
                      </div>
                      <div className="category-progress">
                        <div className="category-fill" style={{ width: `${percentage}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Strengths */}
            {results.strengths.length > 0 && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="url(#strengthGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="strengthGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981"/>
                        <stop offset="100%" stopColor="#059669"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Your Strengths
                </h3>
                <div className="strength-cards">
                  {results.strengths.map((strength, idx) => (
                    <div key={idx} className="strength-card">
                      <div className="strength-header">
                        <h4>{strength.category}</h4>
                        <span className="strength-badge">{strength.percentage}%</span>
                      </div>
                      <p>You performed excellently in this area ({strength.correct}/{strength.total} correct)</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weaknesses */}
            {results.weaknesses.length > 0 && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="url(#weaknessGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="weaknessGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="100%" stopColor="#d97706"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Areas for Improvement
                </h3>
                <div className="weakness-cards">
                  {results.weaknesses.map((weakness, idx) => (
                    <div key={idx} className="weakness-card">
                      <div className="weakness-header">
                        <h4>{weakness.category}</h4>
                        <span className="weakness-badge">{weakness.percentage}%</span>
                      </div>
                      <p>Focus on improving this area ({weakness.correct}/{weakness.total} correct)</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="results-section">
              <h3 className="section-subtitle">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="url(#recommendGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="recommendGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6"/>
                      <stop offset="100%" stopColor="#2563eb"/>
                    </linearGradient>
                  </defs>
                </svg>
                Next Steps
              </h3>
              <div className="recommendations">
                <div className="recommendation-card">
                  <h4>📚 Continue Learning</h4>
                  <p>Use the AI Career Toolkit to get personalized course recommendations based on your weak areas.</p>
                  <button className="toolkit-link" onClick={() => navigate('/ai-improvement/ai-career-toolkit')}>
                    Go to AI Career Toolkit →
                  </button>
                </div>
                
                {!results.passed && (
                  <div className="recommendation-card">
                    <h4>🔄 Practice More</h4>
                    <p>Review the detailed answers below and retake the test to improve your score.</p>
                  </div>
                )}

                <div className="recommendation-card">
                  <h4>🗺️ Plan Your Career</h4>
                  <p>Create a structured roadmap from your current position to your dream role.</p>
                  <button className="career-map-link" onClick={() => navigate('/ai-improvement/ai-career-map')}>
                    Go to AI Career Map →
                  </button>
                </div>
              </div>
            </div>

            {/* Detailed Review */}
            <div className="results-section">
              <h3 className="section-subtitle">Detailed Answer Review</h3>
              <div className="detailed-review">
                {results.detailedAnswers.map((answer, idx) => (
                  <div key={idx} className={`review-card ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="review-header">
                      <span className="review-number">Question {answer.questionId}</span>
                      <span className={`review-status ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                        {answer.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                      </span>
                    </div>
                    <p className="review-question">{answer.question}</p>
                    <div className="review-answers">
                      <div className="review-answer">
                        <strong>Your Answer:</strong> 
                        <span className={answer.isCorrect ? 'correct-answer' : 'wrong-answer'}>
                          {answer.userAnswer || 'Not answered'}
                        </span>
                      </div>
                      {!answer.isCorrect && (
                        <div className="review-answer">
                          <strong>Correct Answer:</strong> 
                          <span className="correct-answer">{answer.correctAnswer}</span>
                        </div>
                      )}
                    </div>
                    <div className="review-explanation">
                      <strong>Explanation:</strong> {answer.explanation}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ═══ RETAKE TEST BUTTON (always visible at bottom of results) ═══ */}
            <div className="retake-test-section">
              <button 
                className="retake-test-button"
                onClick={() => {
                  setTestData(null);
                  setShowResults(false);
                  setResults(null);
                  setUserAnswers({});
                  setCurrentQuestionIndex(0);
                  // Scroll to the input section
                  setTimeout(() => {
                    inputSectionRef.current?.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }, 100);
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Retake Test
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default MCQTest;
